import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { DevTokenInfo, CreateTokenResponse } from '../api'

function formatDate(iso: string | null): string {
    if (!iso) return '—'
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function PlaintextBox({ result, onDismiss }: { result: CreateTokenResponse; onDismiss: () => void }) {
    const [copied, setCopied] = useState(false)

    function copy() {
        void navigator.clipboard.writeText(result.plaintext)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <div className="rounded-xl border border-amber-600/40 bg-amber-500/10 p-5 mb-6">
            <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                    <p className="text-sm font-semibold text-amber-300">Token created — save it now</p>
                    <p className="text-xs text-amber-400/80 mt-0.5">This token will not be shown again.</p>
                </div>
                <button
                    onClick={onDismiss}
                    className="text-slate-400 hover:text-slate-200 text-xs rounded-lg border border-slate-700 px-3 py-1.5 hover:bg-slate-800 transition-colors shrink-0"
                >
                    Done
                </button>
            </div>
            <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-sm text-emerald-300 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 break-all">
                    {result.plaintext}
                </code>
                <button
                    onClick={copy}
                    className="shrink-0 rounded-lg border border-slate-700 px-3 py-2.5 text-xs font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                >
                    {copied ? 'Copied!' : 'Copy'}
                </button>
            </div>
        </div>
    )
}

function TokenRow({ token, onReissue, onRevoke, reissuingId, revokingId }: {
    token: DevTokenInfo
    onReissue: (id: string) => void
    onRevoke: (id: string) => void
    reissuingId: string | null
    revokingId: string | null
}) {
    const [confirmRevoke, setConfirmRevoke] = useState(false)

    return (
        <tr className="transition-colors hover:bg-slate-900/30">
            <td className="py-3 px-4">
                <div className="text-sm font-medium text-slate-200">{token.name}</div>
            </td>
            <td className="py-3 px-4 text-xs text-slate-400">{token.developerEmail}</td>
            <td className="py-3 px-4 text-xs text-slate-400">{formatDate(token.createdAt)}</td>
            <td className="py-3 px-4 text-xs text-slate-500">{formatDate(token.lastUsedAt)}</td>
            <td className="py-3 px-4">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onReissue(token.id)}
                        disabled={reissuingId === token.id || revokingId === token.id}
                        className="text-xs rounded-lg border border-slate-700 px-2.5 py-1 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors disabled:opacity-40"
                    >
                        {reissuingId === token.id ? 'Rotating…' : 'Reissue'}
                    </button>

                    {confirmRevoke ? (
                        <div className="flex items-center gap-1.5">
                            <button
                                onClick={() => { onRevoke(token.id); setConfirmRevoke(false) }}
                                disabled={revokingId === token.id}
                                className="text-xs rounded-lg border border-red-700/60 px-2.5 py-1 text-red-400 hover:bg-red-900/30 transition-colors disabled:opacity-40"
                            >
                                {revokingId === token.id ? 'Revoking…' : 'Confirm'}
                            </button>
                            <button
                                onClick={() => setConfirmRevoke(false)}
                                className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setConfirmRevoke(true)}
                            disabled={revokingId === token.id}
                            className="text-xs rounded-lg border border-slate-700 px-2.5 py-1 text-red-400/70 hover:border-red-700/60 hover:text-red-400 hover:bg-red-900/20 transition-colors disabled:opacity-40"
                        >
                            Revoke
                        </button>
                    )}
                </div>
            </td>
        </tr>
    )
}

export function DevTokensPage() {
    const qc = useQueryClient()

    const { data: tokens, isLoading, isError, error, refetch, isFetching } = useQuery({
        queryKey: ['dev-tokens'],
        queryFn: releaseApi.listTokens,
    })

    const [createName, setCreateName] = useState('')
    const [lastCreated, setLastCreated] = useState<CreateTokenResponse | null>(null)
    const [reissuingId, setReissuingId] = useState<string | null>(null)
    const [revokingId, setRevokingId] = useState<string | null>(null)

    const createMutation = useMutation({
        mutationFn: (name: string) => releaseApi.createToken(name),
        onSuccess: (result) => {
            setLastCreated(result)
            setCreateName('')
            void qc.invalidateQueries({ queryKey: ['dev-tokens'] })
        },
    })

    async function handleReissue(id: string) {
        setReissuingId(id)
        setLastCreated(null)
        try {
            const result = await releaseApi.reissueToken(id)
            setLastCreated(result)
            void qc.invalidateQueries({ queryKey: ['dev-tokens'] })
        } finally {
            setReissuingId(null)
        }
    }

    async function handleRevoke(id: string) {
        setRevokingId(id)
        try {
            await releaseApi.revokeToken(id)
            void qc.invalidateQueries({ queryKey: ['dev-tokens'] })
        } finally {
            setRevokingId(null)
        }
    }

    function handleCreate(e: React.FormEvent) {
        e.preventDefault()
        if (!createName.trim()) return
        createMutation.mutate(createName.trim())
    }

    return (
        <>
            <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-200">Developer Tokens</span>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                        <button
                            onClick={() => void refetch()}
                            disabled={isFetching}
                            className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors disabled:opacity-40"
                        >
                            {isFetching ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
                <p className="text-sm text-slate-400 mb-6">
                    Issue <code className="font-mono text-slate-300 bg-slate-800 rounded px-1.5 py-0.5 text-xs">mq_*</code> Bearer tokens for Claude MCP and other developer tools.
                    Include your name in the token name so admins can identify ownership.
                </p>

                {lastCreated && (
                    <PlaintextBox result={lastCreated} onDismiss={() => setLastCreated(null)} />
                )}

                {/* Create form */}
                <form onSubmit={handleCreate} className="flex items-center gap-3 mb-8">
                    <input
                        type="text"
                        value={createName}
                        onChange={e => setCreateName(e.target.value)}
                        placeholder='e.g. Ben Rimmer – Claude MCP'
                        className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-500"
                    />
                    <button
                        type="submit"
                        disabled={!createName.trim() || createMutation.isPending}
                        className="rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium px-4 py-2 transition-colors disabled:opacity-40 shrink-0"
                    >
                        {createMutation.isPending ? 'Creating…' : 'Create token'}
                    </button>
                </form>

                {createMutation.isError && (
                    <div className="rounded-xl border border-red-800 bg-red-950/50 p-4 text-red-300 text-sm mb-6">
                        {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create token'}
                    </div>
                )}

                {isLoading && (
                    <div className="flex items-center justify-center h-32">
                        <div className="text-slate-500 text-sm animate-pulse">Loading tokens…</div>
                    </div>
                )}

                {isError && (
                    <div className="rounded-xl border border-red-800 bg-red-950/50 p-6 text-red-300 text-sm">
                        Failed to load tokens:{' '}
                        {error instanceof Error ? error.message : 'Unknown error'}
                    </div>
                )}

                {tokens && (
                    <div className="rounded-xl border border-slate-800 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-900 border-b border-slate-800">
                                    <th className="py-2.5 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Name</th>
                                    <th className="py-2.5 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Developer</th>
                                    <th className="py-2.5 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Created</th>
                                    <th className="py-2.5 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Last Used</th>
                                    <th className="py-2.5 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800">
                                {tokens.map(token => (
                                    <TokenRow
                                        key={token.id}
                                        token={token}
                                        onReissue={handleReissue}
                                        onRevoke={handleRevoke}
                                        reissuingId={reissuingId}
                                        revokingId={revokingId}
                                    />
                                ))}
                                {tokens.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="py-8 text-center text-slate-500 text-sm">
                                            No tokens yet. Create one above.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </>
    )
}
